import React, { useEffect, useState } from 'react'
import {
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  Checkbox,
  Box,
  IconButton,
  Tooltip,
  Menu,
  MenuItem, Typography, Container,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { EnhancedTableProps } from '@propTypes/index';
import { useTableStore } from 'state/TableStore';
import { useLocation } from 'react-router-dom';
import { CustomFilter } from './CustomFilter';
import { extractPageName } from '../helpers';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FilterListIcon from '@mui/icons-material/FilterList';
import { useMUITheme } from 'theme/ThemeProvider';
import { PageNames } from 'interfaces/config';

export const CustomTableHead: React.FC<EnhancedTableProps> = (props: EnhancedTableProps) => {
  const {
    numSelected,
    rowCount,
    label,
    handleInternalSelectAll,
    hoverLabel,
    setHoverLabel,
    selectAllState,
    currentPage,
    visibleRows,
    tableName,
    tableConfig,
    tableBgColor,
    isCurrentPageAllSelected,
    isCurrentPagePartiallySelected
  } = props;

  const { tableHeaderCells, tableHeaderSorting } = tableConfig![tableName!]
  const columnState = useTableStore(state => state.columnState)
  const headerButtonConfig = useTableStore(state => state.headerButtonConfig)
  const setHeaderButtonConfig = useTableStore(state => state.setHeaderButtonConfig)
  const customFilterAnchorEl = useTableStore(state => state.customFilterAnchorEl)
  const setCustomFilterAnchorEl = useTableStore(state => state.setCustomFilterAnchorEl)
  const setCurrentDataIndex = useTableStore(state => state.setCurrentDataIndex)
  const setSelected = useTableStore(state => state.setSelected)
  const currentDataIndex = useTableStore(state => state.currentDataIndex)

  const locationObj = useLocation()
  const pageName = extractPageName(locationObj.pathname)
  const [, setChecked] = React.useState(false)
  const [hoverFullName, setHoverFullName] = useState(false)
  const [sortSelectAnchorEl, setSortSelectAnchorEl] = useState(null)
  const { mode } = useMUITheme();
  const isDark = mode === 'dark';
  const openFilterMenu = Boolean(sortSelectAnchorEl)
  const popoverId = openFilterMenu ? 'actions-popover' : undefined;
  const pagination = useTableStore(state => state.pagination)
  const handleSortRequest = useTableStore(state => state.handleSortRequest)

  const { orderBy, order } = pagination[tableName!]

  const createSortHandler = (event: any, property: any) => {
    console.log(`debug get order from pagination sort createSortHandler running property: ${property}`)
    console.log('debug user management full name sort createSortHandler: ', { property, orderBy })

    if (property === "fullName" && pageName !== PageNames.USER_MANAGEMENT) {
      property = orderBy
    }
    handleClose(event)
    if (event.target.checked !== true && event.target.checked !== false) {
      handleSortRequest(tableName, property);
    }
  };

  const handleBeforeSorting = (e: React.MouseEvent<HTMLButtonElement>, cell: any, debugLabel: string) => {
    console.log(`debug ${debugLabel}: `, { renderCustomSortIcon: cell.renderCustomSortIcon, cell, event: e })

    if (isCellDisabled(cell.dataIndex)) return

    if (cell.renderCustomSortIcon) {
      customIconHandler(e, cell.dataIndex)
      return
    }

    if (((cell.dataIndex !== "fullName" && pageName !== "user-management") || pageName === "user-management")) {
      if (e.type === "click") createSortHandler(e, cell.dataIndex)
      else handleKeyDown(e, cell.dataIndex)
    }
  }


  const headerCellsLength = tableHeaderCells?.length || 0
  const widthPercentage = 100 / headerCellsLength

  const tableHeadCellStyle = {
    backgroundColor: tableName === "document-upload" || tableName === "email-delivery" ? isDark ? "#282933" : "#F8F9FB" : tableBgColor,
    height: "56px",
    minWidth: (label !== "batch-printing" && tableName !== "document-upload") ? `${widthPercentage}%` : "",
    top: 0,
    zIndex: 2,
  }

  //Calculate checked status 
  useEffect(() => {
    if (selectAllState?.pages.some((element: any) => element.page === currentPage)) {
      setChecked(selectAllState?.pages.find((element: any) => element.page === currentPage).selectAll)
    } else {
      setChecked(false)
    }
  }, [currentPage, selectAllState?.pages])

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = visibleRows.map((n: any) => pageName === "batch-printing" ? n.transactionNumber : n.key);
      if (newSelected.length > 0) {
        setHeaderButtonConfig({ ...headerButtonConfig, disabled: false })
      } else {
        setHeaderButtonConfig({ ...headerButtonConfig, disabled: true })
      }
      setSelected(newSelected);
      return;
    } else {
      setSelected([]);
    }
  };

  const customIconHandler = (e: any, cell: string) => {
    console.log(`debug ONLY customFilter  icon handler cell ${cell} event: `, e)
    e.stopPropagation()
    if (pageName.includes("rapback") && !pageName.includes("summary")) {
      setSortSelectAnchorEl(e.target)
    } else {
      setCustomFilterAnchorEl(e.target)
    }
    setCurrentDataIndex(cell)
  }

  const tablesToPad = ["transaction-summary", "rapback-validations", "rapback-prenotifications", "rapback-summary", "agency-management", "user-management", "applicant-details"]

  const handleClose = (e) => {
    e.stopPropagation()
    setSortSelectAnchorEl(null)
  }

  const handleKeyDown = (event: any, property: string) => {
    if (event.key === 'Enter') {
      createSortHandler(event, property)
    }
  };

  const SortSelect: React.FC<any> = ({ dataIndex }) => {
    return (
      <Menu
        elevation={10}
        onClose={(e) => handleClose(e)}
        slotProps={{ paper: { sx: { width: "200px", minWidth: 'max-content', backgroundColor: "background.default", border: "none", display: 'flex', flexDirection: "column" } } }}
        id={popoverId}
        open={openFilterMenu && currentDataIndex === dataIndex}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorEl={sortSelectAnchorEl}
        aria-label='Sort option'
      >

        <Container disableGutters onMouseLeave={(e) => handleClose(e)} data-testid="custom-table-header">
          <MenuItem onClick={(e) => createSortHandler(e, "firstName")} onKeyDown={(e) => handleKeyDown(e, "firstName")} sx={{ paddingLeft: 0, paddingRight: 0 }} data-testid="sort-firstname-menu-item">
            <Container disableGutters sx={{ width: "100%", display: "flex", justifyContent: "space-evenly" }}>
              <Typography>Sort by First Name</Typography>
              {
                order === "asc"
                  ? <Tooltip title="sort dsc">
                    <ArrowUpwardIcon sx={{ cursor: "pointer", height: "20px", width: "20px", color: 'text.secondary' }} />
                  </Tooltip>
                  : <Tooltip title="sort asc">
                    <ArrowDownwardIcon sx={{ cursor: "pointer", height: "20px", width: "20px", color: 'text.secondary' }} />
                  </Tooltip>
              }
            </Container>
          </MenuItem>

          <MenuItem onClick={(e) => createSortHandler(e, "lastName")} onKeyDown={(e) => handleKeyDown(e, "lastName")} sx={{ paddingLeft: 0, paddingRight: 0 }} data-testid="sort-lastname-menu-item">
            <Container disableGutters sx={{ display: "flex", justifyContent: "space-evenly" }}>
              <Typography>Sort by Last Name</Typography>
              {
                order === "asc"
                  ? <Tooltip title="sort dsc">
                    <ArrowUpwardIcon onKeyDown={(e) => handleKeyDown(e, "lastName")} sx={{ cursor: "pointer", height: "20px", width: "20px", color: 'text.secondary' }} />
                  </Tooltip>
                  : <Tooltip title="sort asc">
                    <ArrowDownwardIcon onKeyDown={(e) => handleKeyDown(e, "lastName")} sx={{ cursor: "pointer", height: "20px", width: "20px", color: 'text.secondary' }} />
                  </Tooltip>
              }
            </Container>
          </MenuItem>
        </Container >
      </Menu >
    )
  }

  const pageIsRbSub = pageName === "rapback-maintenance" || pageName === "rapback-prenotifications" || pageName === "rapback-validations"

  const handleMouseEnter = (cell) => {
    setCurrentDataIndex(cell.dataIndex)
    if (pageName !== "applicant-details" && tableName !== 'rb-sub-audit-history' && tableName !== "reject-codes") {
      if (pageName === "user-management") {
        if (cell.dataIndex !== "securityLevel" && cell.dataIndex !== "status") {
          setHoverLabel(cell.dataIndex)
        }
      } else if (pageIsRbSub) {
        if (cell.dataIndex !== "fullName") {
          setHoverLabel(cell.dataIndex)
        } else {
          setHoverFullName(true)
        }
      } else {
        setHoverLabel(cell.dataIndex)
      }
    }
  }

  const handleMouseLeave = (cell) => {
    setCurrentDataIndex("")
    if (pageName !== "applicant-details" && tableName !== 'rb-sub-audit-history' && tableName !== "reject-codes") {
      if (pageName === "user-management") {
        if (cell.dataIndex !== "securityLevel" && cell.dataIndex !== "status") {
          setHoverLabel("")
        }
      } else if (pageIsRbSub) {
        if (cell.dataIndex !== "fullName") {
          setHoverLabel("")
        } else {
          setHoverFullName(false)
        }
      } else {
        setHoverLabel("")
      }
    }
  }

  console.log('columnState.page[pageName][tableName!]', tableHeaderCells, columnState.page[pageName][tableName!]?.columnVisibility);

  const isCellDisabled = (dataIndex: string) => {
    let disabledCells = ["hitNotHit", "actions"]
    let isSortDisabled = !tableHeaderSorting ? true : false

    return disabledCells.includes(dataIndex) || isSortDisabled

  }

  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth)

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, []);

  const columnWidths = windowWidth > 1200
    ? ['10%', '60%', '30%']
    : windowWidth > 800
      ? ['15%', '50%', '35%']
      : ['20%', '50%', '30%'];

  return (
    <TableHead sx={{ backgroundColor: "black" }}>
      <TableRow >
        {
          tableHeaderCells?.map((cell: any, index: number) => {
            const isUserManagement = pageName === "user-management"
            const isStatusCell = cell.dataIndex === "status"
            const isSecurityCell = cell.dataIndex === "securityLevel"
            const hasHeaderActions = isUserManagement && (isStatusCell || isSecurityCell)
            const isAscForCell = order === "asc" && orderBy === cell.dataIndex
            const isAscForFullName = order === "asc"
              && cell.dataIndex === "fullName"
              && (orderBy === "firstName" || orderBy === "lastName")
            const isAsc = isAscForCell || isAscForFullName

            return (
              columnState.page[pageName][tableName!]?.columnVisibility[cell.dataIndex] && (
                <TableCell
                  role="rowheader"
                  aria-label={cell.label}
                  sx={[(label !== "batch-printing" || tableName !== "document-upload")
                    ? cell.dataIndex === 'actions' || cell.dataIndex === "checkbox"
                      ? { width: columnWidths[cell.dataIndex], backgroundColor: tableName === "document-upload" ? isDark ? "#282933" : "#F8F9FB" : tablesToPad.includes(pageName) ? "common.black" : "background.default" }
                      : tableHeadCellStyle
                    : { paddingLeft: 0 },
                  { position: "relative", overflow: "hidden", alignItems: 'center' }]}
                  key={`cell-${cell.dataIndex}`}
                  align={'left'}
                  padding={'none'}
                  sortDirection={orderBy === cell.dataIndex ? order : false}
                  data-testid={`table-header-cell-${cell.dataIndex}`}
                >
                  <TableSortLabel
                    disabled={cell.dataIndex === "actions" || !tableHeaderSorting ? true : false}
                    key={`${tableName}-${cell.dataIndex}`}
                    hideSortIcon={hasHeaderActions ? true : cell.dataIndex !== hoverLabel}
                    onMouseEnter={() => handleMouseEnter(cell)}
                    onMouseLeave={() => handleMouseLeave(cell)}
                    sx={{
                      width: "100%",
                      height: "100%",
                      fontSize: `0.875rem`,
                      pl: '1rem',
                      '&.MuiTableSortLabel-root': {
                        cursor: !tableHeaderSorting ? "default" : "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        paddingLeft: 0,
                        position: "relative",
                        overflow: "hidden",
                        '&:focus': {
                          outline: 'none'
                        },
                        '&:focus-visible::after': {
                          content: '""',
                          position: 'absolute',
                          top: '2px',
                          left: '2px',
                          right: '2px',
                          bottom: '2px',
                          border: '2px solid #430099',
                          borderRadius: '4px',
                          pointerEvents: 'none'
                        }
                      }
                    }}
                    active={orderBy === cell.dataIndex || ((orderBy === "firstName" || orderBy === "lastName") && cell.dataIndex === "fullName")}
                    direction={orderBy === cell.dataIndex ? order : "asc"}
                    tabIndex={hasHeaderActions ? -1 : 0}
                    onClick={(e: any) => {
                      if (e.detail === 0) return // skip keyboard click
                      if (hasHeaderActions) return
                      handleBeforeSorting(e, cell, 'click label')
                    }}
                    onKeyDown={(e: any) => {
                      if (hasHeaderActions) return
                      if (e.key !== 'Enter' && e.key !== ' ') return
                      handleBeforeSorting(e, cell, 'label keydown')
                    }}
                    IconComponent={() => {
                      if (hasHeaderActions)
                        return null

                      const fullNameSortLabel = cell.dataIndex === "fullName" && !isUserManagement
                        ? orderBy === "firstName" ? "first name" : orderBy === "lastName" ? "last name" : ""
                        : ""

                      return (
                        <Tooltip title={`sort ${fullNameSortLabel} ${isAsc ? "dsc" : "asc"}`}>
                          {isAsc
                            ? <ArrowUpwardIcon sx={{ marginRight: 2, height: `20px`, width: `20px`, color: 'text.secondary' }} />
                            : <ArrowDownwardIcon sx={{ marginRight: 2, height: `20px`, width: `20px`, color: 'text.secondary' }} />
                          }
                        </Tooltip>
                      )
                    }}
                  >
                    {
                      orderBy === cell.id
                        ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === "desc" ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        )
                        : null
                    }
                    <Box sx={[
                      cell.dataIndex === "fullName" || (pageName === "user-management" && cell.dataIndex === "status") || (pageName === "user-management" && cell.dataIndex === "securityLevel") || cell.dataIndex === "actions"
                        ? { display: "flex", width: "100%", justifyContent: "space-between", alignItems: 'center' }
                        : { display: "flex", width: "90%", justifyContent: "space-between", paddingLeft: 0, paddingRight: 6, alignItems: 'center' },
                      hasHeaderActions && {
                        '& .header-double-actions': {
                          opacity: 0,
                          pointerEvents: 'none',
                          transition: 'opacity 120ms ease-in-out',
                        },
                        '&:hover .header-double-actions, &:focus-within .header-double-actions, &[data-ordered="true"] .header-double-actions': {
                          opacity: 1,
                          pointerEvents: 'auto',
                        },
                      }
                    ]}
                      data-ordered={hasHeaderActions && orderBy === cell.dataIndex ? "true" : undefined}
                    >
                      {
                        // select all button 
                        (label !== "batch-printing" || tableName !== "document-upload") && cell.dataIndex === "checkbox"
                          ?
                          <Checkbox key={cell.dataIndex}
                            aria-label='Select all'
                            color="primary"
                            indeterminate={pageName === "batch-printing" ? isCurrentPagePartiallySelected : numSelected > 0 && numSelected < rowCount}
                            checked={
                              pageName === "batch-printing" && isCurrentPageAllSelected !== undefined
                                ? isCurrentPageAllSelected
                                : numSelected === 0 ? false :
                                  selectAllState?.pages.find((element: any) => element.page === currentPage)?.selectAll || false ? numSelected === rowCount : true
                            }
                            onChange={(e) => { e.preventDefault(); e.stopPropagation(); handleSelectAll(e); handleInternalSelectAll(e) }}
                          />
                          : null
                      }
                      <Typography variant="subtitle2" sx={[
                        { fontSize: `14px` },
                        {
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }
                      ]}>
                        {cell.label}
                      </Typography>
                      {
                        tableName!.includes('rapback') && !tableName!.includes("summary") && cell.dataIndex === "fullName" && hoverFullName &&
                        <MoreVertIcon onClick={(e) => customIconHandler(e, cell.dataIndex)} />

                      }
                      {isUserManagement && <>
                        {
                          isStatusCell && (
                            <>
                              <IconButton
                                aria-label="Sort by status"
                                onClick={(e) => createSortHandler(e, cell.dataIndex)}
                                onKeyDown={(e) => {
                                  if (e.key !== 'Enter' && e.key !== ' ') return
                                  e.preventDefault()
                                  createSortHandler(e, cell.dataIndex)
                                }}
                                size="small"
                                className="header-double-actions"
                              >

                                {isAsc
                                  ? <Tooltip title="sort dsc"><ArrowUpwardIcon sx={{ height: `20px`, width: `20px`, color: 'text.secondary' }} /></Tooltip>
                                  : <Tooltip title="sort asc"><ArrowDownwardIcon sx={{ height: `20px`, width: `20px`, color: 'text.secondary' }} /></Tooltip>
                                }
                              </IconButton>
                              <IconButton
                                aria-label="Open user status filter options"
                                id="user-status-filter-button"
                                onClick={(e) => customIconHandler(e, cell.dataIndex)}
                                onKeyDown={(e) => {
                                  if (e.key !== 'Enter' && e.key !== ' ') return
                                  e.preventDefault()
                                  customIconHandler(e, cell.dataIndex)
                                }}
                                size="small"
                                className="header-double-actions"
                              >
                                <Tooltip title="filter status"><FilterListIcon /></Tooltip>
                              </IconButton>
                            </>
                          )
                        }
                        {
                          isSecurityCell && (
                            <>
                              <IconButton
                                aria-label="Sort by security"
                                onClick={(e) => createSortHandler(e, cell.dataIndex)}
                                onKeyDown={(e) => {
                                  if (e.key !== 'Enter' && e.key !== ' ') return
                                  e.preventDefault()
                                  createSortHandler(e, cell.dataIndex)
                                }}
                                size="small"
                                className="header-double-actions"
                              >
                                {isAsc
                                  ? <Tooltip title="sort dsc"><ArrowUpwardIcon sx={{ height: `20px`, width: `20px`, color: 'text.secondary' }} /></Tooltip>
                                  : <Tooltip title="sort asc"><ArrowDownwardIcon sx={{ height: `20px`, width: `20px`, color: 'text.secondary' }} /></Tooltip>
                                }
                              </IconButton>
                              <IconButton
                                aria-label="Open user security filter options"
                                id="user-security-filter-button"
                                onClick={(e) => customIconHandler(e, cell.dataIndex)}
                                onKeyDown={(e) => {
                                  if (e.key !== 'Enter' && e.key !== ' ') return
                                  e.preventDefault()
                                  customIconHandler(e, cell.dataIndex)
                                }}
                                size="small"
                                className="header-double-actions"
                              >
                                <Tooltip title="filter security"><FilterListIcon /></Tooltip>
                              </IconButton>
                            </>
                          )
                        }
                      </>}
                    </Box>
                    {
                      <CustomFilter
                        tableName={tableName}
                        tableConfig={tableConfig}
                        index={index}
                        key={cell.dataIndex}
                        tableFilter={true}
                        enableHeaderButton={false}
                        enableFooterButtons={false}
                        dataIndex={cell.dataIndex}
                        customFilterAnchorEl={customFilterAnchorEl}
                        setCustomFilterAnchorEl={setCustomFilterAnchorEl} />
                    }
                    {
                      <SortSelect dataIndex={cell.dataIndex} />
                    }
                  </TableSortLabel>
                </TableCell>
              ))
          }
          )
        }
      </TableRow>
    </TableHead >
  );
}
